<template>
  <div class="back-button-container" :class="containerClasses" :data-platform="platform">
    <button
      type="button"
      class="back-button"
      :class="buttonClasses"
      :disabled="disabled"
      @click="handleClick"
    >
      <img class="back-button__icon" :src="iconSrc" alt="Go back" />
    </button>
  </div>
</template>

<script>
import { useNavigation } from '@/composables/useNavigation';
import { isIosNativeOnly, isAndroidNativeOnly, isTablet } from '@/utils/platform-check';

export default {
  name: 'BackButton',
  props: {
    /**
     * The app has two back buttons, chosen by what is behind them:
     * - 'default': solid beige circle, dark arrow. Light pages, and photos —
     *   it stays legible over a bright image, where a white arrow washes out.
     * - 'on-dark': translucent white circle, white arrow. Dark and coloured
     *   surfaces: Next Steps dark mode, a board with light theme text, the BF2026
     *   navy band.
     */
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'on-dark'].includes(value),
    },
    /**
     * Custom icon path. Avoid: pick a `variant` instead, so every back button
     * stays one of the two above.
     */
    icon: {
      type: String,
      default: null,
    },
    /**
     * Disable iOS safe area top padding
     */
    disableSafeArea: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable the button
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Compact mode - removes container padding, for use inside headers/toolbars
     */
    compact: {
      type: Boolean,
      default: false,
    },
    /**
     * Custom back handler function
     * If provided, this will be called instead of default navigation
     * Use this for special cases like PIN overlays or multi-step flows
     * @type {Function}
     */
    customBack: {
      type: Function,
      default: null,
    },
    /**
     * Fallback route when no browser history is available
     * Used for deep links, notifications, or direct URL access
     * @type {String|Object}
     */
    fallbackRoute: {
      type: [String, Object],
      default: '/home',
    },
  },
  emits: ['click', 'before-navigate'],
  setup(props, { emit }) {
    const { goBack } = useNavigation();

    const handleClick = event => {
      if (props.disabled) return;

      // Emit click event for tracking/analytics
      emit('click', event);

      // Emit before-navigate event (can be used to prevent navigation)
      const shouldNavigate = emit('before-navigate', event);
      if (shouldNavigate === false) return;

      // Use custom handler if provided (for special cases)
      if (props.customBack) {
        props.customBack(event);
        return;
      }

      // Use standard stack-based navigation with intelligent fallback
      goBack(props.fallbackRoute);
    };

    return {
      handleClick,
    };
  },
  computed: {
    platform() {
      if (isIosNativeOnly()) return 'ios';
      if (isAndroidNativeOnly()) return 'android';
      return 'web';
    },
    iconSrc() {
      if (this.icon) {
        return this.icon;
      }
      if (this.variant === 'on-dark') return '/assets/icons/back-button-on-dark.svg';
      return '/assets/icons/back-button.svg';
    },
    containerClasses() {
      return {
        'back-button-container--with-safe-area': !this.disableSafeArea && !this.compact,
        'back-button-container--compact': this.compact,
        'back-button-container--ios': isIosNativeOnly(),
        'back-button-container--android': isAndroidNativeOnly(),
        'back-button-container--tablet': isTablet(),
      };
    },
    buttonClasses() {
      return {
        'back-button--default': this.variant === 'default',
        'back-button--on-dark': this.variant === 'on-dark',
        'back-button--disabled': this.disabled,
      };
    },
  },
};
</script>

<style scoped>
.back-button-container {
  width: 100%;
  padding-top: 0px;
  padding-bottom: 0px;
  /* Default: no negative margin for web */
  margin-top: 0px;
}

/* iOS-specific negative margin */
.back-button-container--ios {
  margin-top: -8px;
}

/* iPad-specific spacing */
.back-button-container--tablet {
  margin-top: 16px;
}

/* Android uses positive margin + safe area */
.back-button-container--android {
  margin-top: 8px;
}

/* Apply safe area padding when enabled */
.back-button-container--with-safe-area {
  padding-top: env(safe-area-inset-top, 0px);
}

/* iOS with safe area: negative margin + safe area padding */
.back-button-container--ios.back-button-container--with-safe-area {
  margin-top: -8px;
}

/* iPad with safe area: use 0px margin + safe area padding */
.back-button-container--tablet.back-button-container--with-safe-area {
  margin-top: env(safe-area-inset-top, 0px);
  padding-top: 0px;
}

/* Android with safe area: positive margin + safe area padding */
.back-button-container--android.back-button-container--with-safe-area {
  margin-top: max(8px, env(safe-area-inset-top, 8px));
  padding-top: 0px; /* Safe area is handled by margin */
}

/* Compact mode - no container padding, for use in headers/toolbars */
.back-button-container--compact {
  width: auto;
  padding: 0;
  margin-top: 0px; /* Reset margin in compact mode */
}

.back-button {
  /* Reset button styles */
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  margin: 0;

  /* Touch target: 44x44px minimum for accessibility */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;

  /* Subtle press animation */
  transition:
    transform 0.15s ease-out,
    opacity 0.15s ease-out;
}

.back-button:active:not(.back-button--disabled) {
  transform: scale(0.95);
}

.back-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.back-button__icon {
  width: 32px;
  height: 32px;
}

/* The on-dark circle fills the whole 44px touch target, as the BF2026 frames draw it. */
.back-button--on-dark .back-button__icon {
  width: 44px;
  height: 44px;
}
</style>
